import { DOMSerializer, type Mark, type Node } from 'prosemirror-model'
import type { Mapping } from 'prosemirror-transform'
import type { EditorView } from 'prosemirror-view'

export type Measure = {
  maxWidth: number
  text: (text: string, marks: ReadonlyArray<Mark>) => number
  // Width of an inline atom (info or reference icon) at a document position
  node: (pos: number, node: Node) => number
}

let canvas: HTMLCanvasElement | null = null

// Structural: prosemirror-state hands plugins and commands its own nested prosemirror-view copy
export type MeasuredView = Pick<EditorView, 'dom' | 'nodeDOM'>

// An unrendered atom, drawn hidden next to the editor with the editor's classes
// - Its icon styles match there as they do inside the editor.
const probeWidth = (view: MeasuredView, node: Node) => {
  const host = view.dom.parentElement
  if (!host) return 0
  const box = document.createElement('div')
  box.className = view.dom.className
  box.style.cssText = 'position: absolute; visibility: hidden; white-space: nowrap'
  const dom = DOMSerializer.fromSchema(node.type.schema).serializeNode(node)
  box.append(dom)
  host.append(box)
  const width = dom instanceof HTMLElement ? dom.getBoundingClientRect().width : 0
  box.remove()
  return width
}

// Measures with the editor's own font on a canvas
// - Bold and italic marks switch to that cut, which is noticeably wider.
// - Without a canvas context it falls back to half an em per character.
// - `mapping` leads positions in a changed doc back to the one the view still shows.
export const canvasMeasure = (
  view: MeasuredView,
  lineWidthEm: number,
  mapping?: Mapping
): Measure => {
  const style = getComputedStyle(view.dom)
  const fontSize = parseFloat(style.fontSize) || 15
  canvas ??= document.createElement('canvas')
  const context = canvas.getContext('2d')
  const toView = mapping?.invert()

  // Only a few mark combinations occur; the canvas re-parses a font on every assignment
  const fonts = new Map<string, string>()
  const font = (marks: ReadonlyArray<Mark>) => {
    const italic = marks.some((mark) => mark.type.name === 'em')
    const bold = marks.some((mark) => mark.type.name === 'strong')
    const key = `${italic}${bold}`
    let value = fonts.get(key)
    if (!value) {
      value = [
        italic ? 'italic' : style.fontStyle,
        bold ? 'bold' : style.fontWeight,
        `${fontSize}px`,
        style.fontFamily,
      ].join(' ')
      fonts.set(key, value)
    }
    return value
  }
  // Every gap measures a space; `context.font` reads back normalised, so track the last one set
  const spaces = new Map<string, number>()
  let lastFont = ''

  return {
    maxWidth: lineWidthEm * fontSize,
    text: (text, marks) => {
      if (!context) return text.length * fontSize * 0.5
      const current = font(marks)
      if (text === ' ' && spaces.has(current)) return spaces.get(current)!
      if (current !== lastFont) context.font = lastFont = current
      const width = context.measureText(text).width
      if (text === ' ') spaces.set(current, width)
      return width
    },
    node: (pos, node) => {
      const shown = toView?.mapResult(pos, 1)
      // Just pasted, not rendered yet
      if (shown?.deleted) return probeWidth(view, node)
      const dom = view.nodeDOM(shown ? shown.pos : pos)
      return dom instanceof HTMLElement ? dom.getBoundingClientRect().width : 0
    },
  }
}
